import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { UserId } from "@pack-it-up/shared";
import { createTemplateSyncStore } from "@pack-it-up/viewmodel";
import { syncCounts, syncEntryKey } from "@pack-it-up/core";
import type { SyncCollection, SyncCounts, SyncEntry, TemplateSummary, TripContents } from "@pack-it-up/core";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useSessionDeps } from "../../app/sessionDeps.js";
import type { ListTab } from "../ListTabs.js";
import type { SyncListMode } from "./syncMode.js";
import type { SyncOnlyInTemplateRow } from "./SyncOnlyInTemplateSection.js";

export interface TripSync {
  // The store itself — the view drives the mode (open / select / cancel / confirm).
  sync: ReturnType<typeof createTemplateSyncStore>;
  open: boolean;
  templateId: TemplateSummary["id"] | null;
  candidates: TemplateSummary[];
  loading: boolean;
  applying: boolean;
  error: string | null;
  confirmOpen: boolean;
  setConfirmOpen: Dispatch<SetStateAction<boolean>>;
  totals: SyncCounts;
  // Present only while the mode is live with a planned draft; the lists render ticks off it.
  listMode: SyncListMode | undefined;
  onlyInTemplate: SyncOnlyInTemplateRow[];
}

// ── T28 sync back to a template (Doc 13) ─────────────────────────────────────
// Sync is a mode over the trip list, entered from the templates modal. The store plans in
// `core` and hands the plan to the composition root's applier; nothing here writes.
export const useTripSync = (actorId: string | null, tab: ListTab): TripSync => {
  const { repos, applyTemplateSyncPlan } = useSessionDeps();
  const sync = useMemo(
    // Non-null in practice: this view only renders once useTripDetail has resolved the actor.
    () => createTemplateSyncStore({ templates: repos.templates, userId: actorId as UserId, applyTemplateSyncPlan }),
    [repos, actorId, applyTemplateSyncPlan],
  );
  // The store owns a templates stream; a superseded one must unsubscribe or its listener
  // keeps firing into state nothing renders. Deliberately not an effect *cleanup*:
  // StrictMode's simulated remount runs cleanups without rebuilding the memo, which would
  // leave the live store disposed. Disposing only the previous one is remount-safe.
  const previousSync = useRef<typeof sync | null>(null);
  useEffect(() => {
    const superseded = previousSync.current;
    previousSync.current = sync;
    if (superseded !== null && superseded !== sync) superseded.getState().dispose();
  }, [sync]);
  const syncOpen = useStoreValue(sync, (s) => s.open);
  const syncTemplateId = useStoreValue(sync, (s) => s.templateId);
  const syncCandidateList = useStoreValue(sync, (s) => s.candidates);
  const syncDraft = useStoreValue(sync, (s) => s.draft);
  const syncChecked = useStoreValue(sync, (s) => s.checked);
  const syncLoading = useStoreValue(sync, (s) => s.loading);
  const syncApplying = useStoreValue(sync, (s) => s.applying);
  const syncError = useStoreValue(sync, (s) => s.error);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  // The selected template's own rows — needed only to name the "Only in template" entries.
  // The store keeps them privately (planner input, not UI state), so the page reads them
  // once more rather than widening the ViewModel's surface.
  const [syncTemplateRows, setSyncTemplateRows] = useState<TripContents | null>(null);
  useEffect(() => {
    if (!syncOpen || syncTemplateId === null) {
      setSyncTemplateRows(null);
      return undefined;
    }
    let alive = true;
    void repos.templates.fetchTemplateRows(syncTemplateId).then((r) => {
      if (alive && r.ok) setSyncTemplateRows(r.value);
    });
    return () => {
      alive = false;
    };
  }, [repos, syncOpen, syncTemplateId]);

  // Trip-row entries, keyed the way the lists look them up.
  const syncEntriesByRow = useMemo(() => {
    const m = new Map<string, SyncEntry>();
    for (const e of syncDraft?.entries ?? []) if (e.tripRowId != null) m.set(`${e.collection}:${e.tripRowId}`, e);
    return m;
  }, [syncDraft]);
  const syncTotals = useMemo(
    () => (syncDraft !== null ? syncCounts(syncDraft, syncChecked) : { updates: 0, adds: 0, removes: 0, conflicts: 0 }),
    [syncDraft, syncChecked],
  );
  const syncListMode = useMemo<SyncListMode | undefined>(() => {
    if (!syncOpen || syncDraft === null) return undefined;
    return {
      entryFor: (collection, id) => syncEntriesByRow.get(`${collection}:${id}`),
      isChecked: (key) => syncChecked.has(key),
      toggle: (key) => sync.getState().toggle(key),
      categoryTickState: (key) => sync.getState().categoryTickState(key),
      toggleCategory: (key) => sync.getState().toggleCategory(key),
    };
  }, [sync, syncOpen, syncDraft, syncChecked, syncEntriesByRow]);
  // Doc 13 §2.3 — template rows with no trip row, split by the tab that owns them.
  const syncOnlyInTemplate = useMemo<SyncOnlyInTemplateRow[]>(() => {
    if (syncDraft === null || syncTemplateRows === null) return [];
    const collection: SyncCollection = tab === "todos" ? "todos" : "items";
    const names = new Map<string, string>(
      [...syncTemplateRows.items, ...syncTemplateRows.todos].map((r) => [r.id as string, r.name]),
    );
    return syncDraft.entries
      .filter((e) => e.status === "templateOnly" && e.collection === collection && e.templateRowId !== undefined)
      .map((e) => ({ key: syncEntryKey(e), name: names.get(e.templateRowId as string) ?? "(removed)", entry: e }));
  }, [syncDraft, syncTemplateRows, tab]);

  return {
    sync,
    open: syncOpen,
    templateId: syncTemplateId,
    candidates: syncCandidateList,
    loading: syncLoading,
    applying: syncApplying,
    error: syncError,
    confirmOpen: syncConfirmOpen,
    setConfirmOpen: setSyncConfirmOpen,
    totals: syncTotals,
    listMode: syncListMode,
    onlyInTemplate: syncOnlyInTemplate,
  };
};
